import { cn } from '@resolute/ui';
import { useReducedMotion } from 'framer-motion';
import { Mic, Search } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';

import { type Suggestion } from './suggestions';

// ── Minimal Web Speech API surface (not in the DOM lib typings) ──────────────
interface SpeechAlternative {
  transcript: string;
}
interface SpeechRecognitionResultLike {
  [index: number]: SpeechAlternative;
  length: number;
  /** False while the engine is still refining this phrase. */
  isFinal: boolean;
}
interface SpeechResult {
  /** `results` is cumulative across events; new entries start here. */
  resultIndex?: number;
  results: ArrayLike<SpeechRecognitionResultLike>;
}
interface SpeechErrorEvent {
  error?: string;
}
interface SpeechRecognitionLike {
  lang: string;
  interimResults: boolean;
  continuous: boolean;
  maxAlternatives?: number;
  start(): void;
  /** Ends capture and delivers what was heard. */
  stop(): void;
  /** Ends capture and discards it. */
  abort?(): void;
  onresult: ((e: SpeechResult) => void) | null;
  onerror: ((e: SpeechErrorEvent) => void) | null;
  onend: (() => void) | null;
}
type SpeechRecognitionCtor = new () => SpeechRecognitionLike;
const getSpeechCtor = (): SpeechRecognitionCtor | undefined => {
  if (typeof window === 'undefined') return undefined;
  const w = window as unknown as {
    SpeechRecognition?: SpeechRecognitionCtor;
    webkitSpeechRecognition?: SpeechRecognitionCtor;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition;
};

/**
 * Spoken-word messages for the failures a visitor can actually act on. Without
 * these the mic just silently stops — a blocked permission, a muted device and
 * a missed phrase all look identical (nothing happens), which is the single
 * biggest reason voice search reads as broken.
 */
const VOICE_ERRORS: Record<string, string> = {
  'not-allowed': 'Microphone blocked. Allow mic access for this site, then tap the mic again.',
  'service-not-allowed':
    'Microphone blocked. Allow mic access for this site, then tap the mic again.',
  'audio-capture': 'No microphone found. Check that one is connected and not in use.',
  network: 'Voice search needs an internet connection.',
  'no-speech': 'Didn’t catch that — tap the mic and speak again.',
};
const VOICE_FALLBACK = 'Voice search didn’t work — tap the mic to try again.';

interface SmartSearchBarProps {
  placeholder: string;
  query: string;
  setQuery: (q: string) => void;
  /** Clears any previously selected location (e.g. from "Near me") once the
   *  user starts typing or dictating their own query. */
  setSelected: (s: Suggestion | null) => void;
  /** Navigate to results. Voice search passes the just-dictated text directly
   *  (rather than relying on `query` state, which hasn't re-rendered into this
   *  component yet at the moment recognition finishes) so it can submit
   *  immediately without the user needing to press Enter. */
  onSubmit: (queryOverride?: string) => void;
}

/**
 * Layer 3 — the global search bar. A plain free-text field (search icon +
 * input + microphone) with no autocomplete or suggestions of any kind —
 * typing and pressing Enter (or the filter bar's Search button) submits the
 * raw query as-is. A completed voice dictation submits automatically.
 */
export function SmartSearchBar({
  placeholder,
  query,
  setQuery,
  setSelected,
  onSubmit,
}: SmartSearchBarProps) {
  const reduce = useReducedMotion();
  const inputRef = useRef<HTMLInputElement>(null);
  const recognitionRef = useRef<SpeechRecognitionLike | null>(null);
  const [listening, setListening] = useState(false);
  /** What the engine has heard so far — shown live in the field while dictating. */
  const [heard, setHeard] = useState('');
  const [voiceError, setVoiceError] = useState<string | null>(null);

  const micSupported = useMemo(() => getSpeechCtor() !== undefined, []);

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      onSubmit();
    }
  };

  // ── Voice search ───────────────────────────────────────────────────────────
  /** Detaches the handlers before ending, so a teardown can't submit or set
   *  state on the way out. */
  const teardown = () => {
    const rec = recognitionRef.current;
    recognitionRef.current = null;
    if (!rec) return;
    rec.onresult = null;
    rec.onerror = null;
    rec.onend = null;
    (rec.abort ?? rec.stop).call(rec);
  };

  /** Abandon the dictation without searching (typing, or unmount). */
  const cancelVoice = () => {
    teardown();
    setListening(false);
    setHeard('');
  };

  const startVoice = () => {
    const Ctor = getSpeechCtor();
    if (!Ctor || listening) return;
    setVoiceError(null);
    setHeard('');

    const rec = new Ctor();
    rec.lang = 'en-IN';
    // Interim results are what make the field fill in as the visitor speaks —
    // the only signal that the mic is genuinely working.
    rec.interimResults = true;
    rec.continuous = false;
    rec.maxAlternatives = 1;

    let settled = '';
    let failed = false;

    rec.onresult = (e) => {
      let live = '';
      // Only walk the new results: `results` is cumulative, so re-reading it
      // from 0 on every event would append the same phrase repeatedly.
      for (let i = e.resultIndex ?? 0; i < e.results.length; i += 1) {
        const result = e.results[i];
        const text = result?.[0]?.transcript ?? '';
        if (result?.isFinal) settled += text;
        else live += text;
      }
      setHeard(`${settled}${live}`.trim());
    };

    rec.onerror = (e) => {
      failed = true;
      // 'aborted' is our own cancel (typing, unmount) — not something to report.
      if (e?.error === 'aborted') return;
      setVoiceError(VOICE_ERRORS[e?.error ?? ''] ?? VOICE_FALLBACK);
    };

    // `onend` always fires exactly once — whether recognition ended on its own
    // silence timeout, on `stop()`, or after an error — so submitting here (and
    // not from `onresult`) means one submit per dictation, never zero or two.
    rec.onend = () => {
      recognitionRef.current = null;
      setListening(false);
      setHeard('');
      const transcript = settled.trim();
      if (transcript) {
        setQuery(transcript);
        setSelected(null);
        onSubmit(transcript);
      } else if (!failed) {
        setVoiceError(VOICE_ERRORS['no-speech']!);
      }
    };

    recognitionRef.current = rec;
    try {
      rec.start();
    } catch {
      // Chrome throws InvalidStateError if a session is somehow still open;
      // without this the button would be stuck in its listening state.
      recognitionRef.current = null;
      setVoiceError(VOICE_FALLBACK);
      return;
    }
    setListening(true);
  };

  /** Tapping the mic while it listens means "I'm done" — `stop()` (not
   *  `abort()`) delivers what was heard, so the search still runs. */
  const stopVoice = () => {
    recognitionRef.current?.stop();
    setListening(false);
  };
  const toggleMic = () => (listening ? stopVoice() : startVoice());

  useEffect(() => cancelVoice, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div
      className={cn(
        // Solid card-surface floating search box. No glass / translucency —
        // a premium, distraction-free field.
        'relative flex items-center gap-2 rounded-xl border border-border bg-card px-3 shadow-md',
        // Premium focus state (replaces the default browser/blue focus ring):
        // a soft brand-coloured border + minimal primary glow.
        'transition-[box-shadow,border-color] duration-200 ease-out-expo',
        'focus-within:border-primary/45 focus-within:shadow-lg focus-within:ring-4 focus-within:ring-primary/10',
      )}
    >
      <Search className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />

      <input
        ref={inputRef}
        type="text"
        aria-label={placeholder}
        autoComplete="off"
        placeholder={listening ? 'Listening…' : placeholder}
        // Always the committed query — never the in-progress transcript, which
        // lives in the status line below. Two writers for one value is how you
        // end up typing "sola" into a half-heard "bopa" and searching
        // "bopasola"; the live transcript is feedback, not input.
        value={query}
        onChange={(e) => {
          // Typing wins over an in-flight dictation rather than fighting it.
          if (listening) cancelVoice();
          setVoiceError(null);
          setQuery(e.target.value);
          setSelected(null);
        }}
        onKeyDown={onKeyDown}
        // Kill the global :focus-visible ring on the input itself — the premium
        // focus state lives on the container (focus-within) above.
        className="h-11 w-full bg-transparent text-base text-foreground outline-none ring-0 placeholder:text-muted-foreground focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0"
      />

      {/* Microphone (voice search). Feature-detected; disabled when unsupported. */}
      <button
        type="button"
        onClick={toggleMic}
        disabled={!micSupported}
        aria-pressed={listening}
        aria-label={
          micSupported
            ? listening
              ? 'Stop voice search'
              : 'Search by voice'
            : 'Voice search is not supported in this browser'
        }
        title={micSupported ? 'Search by voice' : 'Voice search is not supported in this browser'}
        className={cn(
          'relative flex size-10 shrink-0 items-center justify-center rounded transition-colors duration-base ease-out-expo focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          listening ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted',
          !micSupported && 'cursor-not-allowed opacity-40 hover:bg-transparent',
        )}
      >
        {listening && !reduce && (
          <span className="absolute inset-0 animate-ping rounded-full bg-primary/40" aria-hidden="true" />
        )}
        <Mic className="relative size-4" aria-hidden="true" />
      </button>

      {/* Voice status — the mic's only way to explain itself, and the reason a
          blocked permission or a missed phrase no longer just looks like a
          dead button. The live region stays mounted (an `aria-live` element
          added at the same time as its text is announced unreliably) and is
          absolutely positioned so a message never shifts the hero's layout. */}
      <p
        role="status"
        aria-live="polite"
        className="pointer-events-none absolute left-0 top-full z-10 mt-2 w-full"
      >
        {(voiceError || listening) && (
          <span
            className={cn(
              'inline-flex max-w-full truncate rounded-md border bg-card px-2.5 py-1.5 text-xs shadow-sm',
              voiceError ? 'border-danger/30 text-danger' : 'border-border text-muted-foreground',
            )}
          >
            {voiceError ?? (
              <>
                Listening…{' '}
                {/* Hidden from the live region: announcing every interim word
                    would talk over the visitor as they speak. */}
                <span aria-hidden="true" className="text-foreground">
                  {heard || 'speak now'}
                </span>
              </>
            )}
          </span>
        )}
      </p>
    </div>
  );
}
